import { AUTH_CONSTANTS, type TokenKey } from '@/constants/auth';

/**
 * Utility functions for token management
 * Centralizes all token-related operations to reduce code duplication
 */

export class TokenManager {
  /**
   * Remove specific token from localStorage
   */
  static removeToken(key: TokenKey): void {
    try {
      localStorage.removeItem(key);
      console.log(`Token removed: ${key}`);
    } catch (error) {
      console.error(`Error removing token ${key}:`, error);
    }
  }

  /**
   * Remove all authentication tokens
   */
  static removeAllTokens(): void {
    console.log('=== TokenManager: Removing all tokens ===');
    
    // Remove specific tokens
    Object.values(AUTH_CONSTANTS.TOKEN_KEYS).forEach(key => {
      this.removeToken(key as TokenKey);
    });

    // Remove additional auth-related keys
    this.removeAuthRelatedKeys();
    
    // Clear session storage
    this.clearSessionStorage();
    
    console.log('=== TokenManager: All tokens removed ===');
  }

  /**
   * Remove all keys matching auth-related patterns
   */
  private static removeAuthRelatedKeys(): void {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && this.isAuthRelatedKey(key)) {
        keysToRemove.push(key);
      }
    }
    
    console.log('Additional auth-related keys to remove:', keysToRemove);
    keysToRemove.forEach(key => {
      console.log(`Removing additional key: ${key}`);
      localStorage.removeItem(key);
    });
  }

  /**
   * Check if key matches auth-related patterns
   */
  private static isAuthRelatedKey(key: string): boolean {
    return AUTH_CONSTANTS.CLEANUP_PATTERNS.some(pattern => 
      key.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  /**
   * Clear session storage
   */
  private static clearSessionStorage(): void {
    try {
      console.log('Clearing sessionStorage...');
      const sessionKeysBefore = Array.from({ length: sessionStorage.length }, (_, i) => 
        sessionStorage.key(i)
      ).filter(Boolean);
      
      console.log('SessionStorage keys before clearing:', sessionKeysBefore);
      sessionStorage.clear();
      console.log('SessionStorage length after clearing:', sessionStorage.length);
    } catch (error) {
      console.error('Error clearing sessionStorage:', error);
    }
  }

  /**
   * Get token value
   */
  static getToken(key: TokenKey): string | null {
    return localStorage.getItem(key);
  }

  /**
   * Set token value
   */
  static setToken(key: TokenKey, value: string): void {
    try {
      localStorage.setItem(key, value);
      console.log(`Token set: ${key}`);
    } catch (error) {
      console.error(`Error setting token ${key}:`, error);
    }
  }

  /**
   * Check if any authentication token exists
   */
  static hasAnyToken(): boolean {
    const accessToken = this.getToken(AUTH_CONSTANTS.TOKEN_KEYS.ACCESS);
    const apiToken = this.getToken(AUTH_CONSTANTS.TOKEN_KEYS.API);
    return !!(accessToken || apiToken);
  }

  /**
   * Get all current localStorage keys for debugging
   */
  static debugLocalStorage(): void {
    console.log('=== DEBUG: localStorage Keys ===');
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        console.log(`Key: ${key}, Value: ${value?.substring(0, 50)}...`);
      }
    }
    console.log('=== END DEBUG ===');
  }
}
